import React, { useEffect, useState, Fragment } from "react";
import {
 Row,
 Col,
 Card,
 Button,
 Modal,
 Form,
 Badge,
 Spinner,
} from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";

const BoqUnitTypeMaster = () => {
 const [list, setList] = useState([]);
 const [filtered, setFiltered] = useState([]);
 const [loading, setLoading] = useState(false);
 const [saving, setSaving] = useState(false);

 const [showModal, setShowModal] = useState(false);
 const [editId, setEditId] = useState(null);
 const [search, setSearch] = useState("");

 const [form, setForm] = useState({
 name: "",
 symbol: "",
 status: "Active",
 });

 /* =========================
 FETCH UNIT TYPES
 ========================= */
 const fetchUnits = async () => {
 setLoading(true);
 try {
 const res = await api.get("/boq-unit-types?includeInactive=true");
 if (res.data.success) {
 setList(res.data.data);
 setFiltered(res.data.data);
 }
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 fetchUnits();
 }, []);

 /* =========================
 SEARCH
 ========================= */
 useEffect(() => {
 if (!search.trim()) {
 setFiltered(list);
 } else {
 const s = search.toLowerCase();
 setFiltered(
 list.filter(
 (i) =>
 i.name.toLowerCase().includes(s) ||
 i.symbol?.toLowerCase().includes(s)
 )
 );
 }
 }, [search, list]);

 /* =========================
 MODAL HANDLERS
 ========================= */
 const openAdd = () => {
 setEditId(null);
 setForm({
 name: "",
 symbol: "",
 status: "Active",
 });
 setShowModal(true);
 };

 const openEdit = (item) => {
 setEditId(item._id);
 setForm({
 name: item.name,
 symbol: item.symbol || "",
 status: item.status,
 });
 setShowModal(true);
 };

 const closeModal = () => {
 if (!saving) setShowModal(false);
 };

 const handleChange = (e) =>
 setForm({ ...form, [e.target.name]: e.target.value });

 /* =========================
 SAVE (CREATE / UPDATE)
 ========================= */
 const handleSave = async () => {
 if (!form.name.trim()) {
 return swal("Validation", "Unit name is required", "warning");
 }

 try {
 setSaving(true);
 if (editId) {
 await api.put(`/boq-unit-types/${editId}`, form);
 } else {
 await api.post("/boq-unit-types", form);
 }

 swal("Success", "BOQ unit saved successfully", "success");
 closeModal();
 fetchUnits();
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Operation failed",
 "error"
 );
 } finally {
 setSaving(false);
 }
 };

 /* =========================
 DEACTIVATE
 ========================= */
 const deactivate = (item) => {
 swal({
 title: `Deactivate "${item.name}"?`,
 text: "This unit will no longer be selectable in BOQ.",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 }).then(async (ok) => {
 if (!ok) return;
 try {
 await api.delete(`/boq-unit-types/${item._id}`);
 fetchUnits();
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Cannot deactivate",
 "error"
 );
 }
 });
 };

 return (
 <Fragment>
 <PageTitle activeMenu="BOQ Unit Master" motherMenu="Masters" />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between">
 <Card.Title>BOQ Units</Card.Title>
 <Button onClick={openAdd}>+ Add Unit</Button>
 </Card.Header>

 <Card.Body>
 <Form.Control
 className="mb-3"
 placeholder="Search by unit name or symbol..."
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 />

 {loading ? (
 <div className="text-center py-5">
 <Spinner animation="border" />
 </div>
 ) : (
 <table className="table table-bordered table-hover">
 <thead className="table-light">
 <tr>
 <th>Unit Name</th>
 <th>Symbol</th>
 <th>Status</th>
 <th width="170">Actions</th>
 </tr>
 </thead>
 <tbody>
 {filtered.map((item) => (
 <tr key={item._id}>
 <td>{item.name}</td>
 <td>{item.symbol || "-"}</td>
 <td>
 <Badge
 bg={
 item.status === "Active"
 ? "success"
 : "secondary"
 }
 >
 {item.status}
 </Badge>
 </td>
 <td>
 <Button
 size="sm"
 variant="warning"
 className="me-2"
 onClick={() => openEdit(item)}
 >
 Edit
 </Button>

 {item.status === "Active" && (
 <Button
 size="sm"
 variant="danger"
 onClick={() => deactivate(item)}
 >
 Deactivate
 </Button>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 )}

 {!loading && filtered.length === 0 && (
 <div className="text-center text-muted py-4">
 No BOQ units found
 </div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* ================= MODAL ================= */}
 <Modal
 show={showModal}
 onHide={closeModal}
 backdrop={saving ? "static" : true}
 >
 <Modal.Header closeButton={!saving}>
 <Modal.Title>
 {editId ? "Edit BOQ Unit" : "Add BOQ Unit"}
 </Modal.Title>
 </Modal.Header>

 <Modal.Body>
 <Form.Group className="mb-3">
 <Form.Label>Unit Name *</Form.Label>
 <Form.Control
 name="name"
 value={form.name}
 onChange={handleChange}
 placeholder="e.g. Kilogram"
 disabled={saving}
 />
 </Form.Group>

 <Form.Group className="mb-3">
 <Form.Label>Symbol</Form.Label>
 <Form.Control
 name="symbol"
 value={form.symbol}
 onChange={handleChange}
 placeholder="e.g. kg"
 disabled={saving}
 />
 </Form.Group>

 <Form.Group>
 <Form.Label>Status</Form.Label>
 <Form.Select
 name="status"
 value={form.status}
 onChange={handleChange}
 disabled={saving}
 >
 <option value="Active">Active</option>
 <option value="Inactive">Inactive</option>
 </Form.Select>
 </Form.Group>
 </Modal.Body>

 <Modal.Footer>
 <Button
 variant="secondary"
 onClick={closeModal}
 disabled={saving}
 >
 Cancel
 </Button>
 <Button onClick={handleSave} disabled={saving}>
 {saving ? "Saving..." : "Save"}
 </Button>
 </Modal.Footer>
 </Modal>
 </Fragment>
 );
};

export default BoqUnitTypeMaster;
